import React, { useCallback, useMemo, useEffect } from 'react';
import { Form, Input, Card, Typography, Row, Col, Divider, Table, Alert } from 'antd';
import { NumberOutlined, CalculatorOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { SUBJECTS, GRADES } from '../../../config';
const { Title, Text } = Typography;

const GradesSection = ({ parentForm }) => {

    const validateGrade = (_, value) => {
        if (value === undefined || value === '') {
            return Promise.resolve();
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
            return Promise.reject('Vui lòng nhập số!');
        }
        if (num < 0 || num > 10) {
            return Promise.reject('Điểm phải từ 0 đến 10!');
        }
        if (value.toString().split('.')[1]?.length > 1) {
            return Promise.reject('Chỉ được nhập tối đa 1 chữ số thập phân!');
        }
        return Promise.resolve();
    };


    const columns = useMemo(() => [
        {
            title: 'Môn học',
            dataIndex: 'subject',
            align: 'center',
            key: 'subject',
            width: '120px',
            fixed: 'left',
            render: (text) => <strong>{text}</strong>
        },
        ...GRADES.map(grade => ({
            title: `Lớp ${grade}`,
            align: 'center',
            dataIndex: `grade_${grade}`,
            key: `grade_${grade}`,
            width: '100px',
            render: (_, record) => (
                <Form.Item
                    name={`${record.key}_${grade}`}
                    validateTrigger="onSubmit"
                    rules={[
                        { required: true, message: `Vui lòng nhập điểm ${record.subject}!` },
                        { validator: validateGrade }
                    ]}
                    style={{ marginBottom: 0 }}
                >
                    <Input
                        placeholder={`Điểm ${record.subject}`}
                        type="number"
                        step="0.1"
                        min="0"
                        style={{ 
                            width: '100%', 
                            textAlign: 'center',
                            fontSize: '16px', // Larger font for better touch targets
                            height: '40px', // Taller input for better touch targets
                            padding: '8px'
                        }}
                        autoComplete="off"
                        onBlur={(e) => {
                            const value = e.target.value.trim();
                            if (value === '') return;
                            
                            const fieldName = `${record.key}_${grade}`;
                            
                            // Kiểm tra nếu là số nguyên (không có dấu chấm)
                            if (/^\d+$/.test(value)) {
                                const numValue = parseInt(value);
                                
                                // Nếu > 10 và <= 100 thì chia cho 10 (ví dụ 65 -> 6.5, 75 -> 7.5)
                                if (numValue > 10 && numValue <= 100) {
                                    const convertedValue = (numValue / 10).toFixed(1);
                                    parentForm.setFieldValue(fieldName, convertedValue);
                                }
                                // Nếu <= 10 thì thêm .0 (ví dụ 6 -> 6.0, 8 -> 8.0, 10 -> 10.0)
                                else if (numValue <= 10) {
                                    parentForm.setFieldValue(fieldName, numValue.toFixed(1));
                                }
                            }
                        }}
                    />
                </Form.Item>
            )
        }))
    ], [validateGrade]);

    // Generate dataSource with exact backend field names
    const dataSource = Object.entries(SUBJECTS).map(([key, value]) => ({
        key, // This is the backend field name (e.g., 'math', 'literature', etc.)
        subject: value,
    }));

    return (
        <div style={{ 
            width: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
        }}>
            <Divider>
                <Title level={4} style={{ 
                    fontSize: 'clamp(1rem, 4vw, 1.5rem)',
                    margin: '0'
                }}>
                    Điểm cả năm các môn học
                </Title>
            </Divider>

            <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                bordered
                scroll={{ 
                    x: 'max-content',
                    y: 'auto'
                }}
                size="middle"
                style={{
                    minWidth: '100%',
                    tableLayout: 'fixed'
                }}
            />
            <Alert
                message={<Text strong>Lưu ý:</Text>}
                description={
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        <li>Thí sinh không học môn nào thì nhập số 0 vào ô tương ứng</li>
                        <li>Đối với thí sinh tốt nghiệp năm 2026, cần hoàn thiện điểm lớp 12 sau khi có điểm thi</li>
                        <li>Hỗ trợ nhập nhanh: Nhập 65 sẽ tự động chuyển thành 6.5, nhập 75 thành 7.5, v.v...</li>
                    </ul>
                }
                type="warning"
                showIcon
                style={{ marginTop: '16px' }}
            />
        </div>
    );
};

GradesSection.propTypes = {
    parentForm: PropTypes.object.isRequired
};

export default React.memo(GradesSection); 